import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import type { RWTable, Table } from "common/data/table";
import hashString from "common/utils/hashString";
import type { AABB3 } from "geom/aabb3Types";
import type { Face, FaceTagBase } from "geom/brep";
import { faceNew, mergeNonAdjacentCoplanarFaces, tagFaces } from "geom/brep";
import { gatherOrientedCoplanarFaceGroups, gatherOrientedCoplanarFaceGroupsFast } from "geom/coplanarFaceGroups";
import { planeEval, planeProject } from "geom/plane";
import { PolygonRoundingMode, SNAP_ROUNDING_TOLERANCE } from "geom/polyclip/polyClip";
import {
  polyLine2EdgeCount,
  polyLine2Area,
  polyLine2RemoveInvalidVertices,
  polyLine2Simplify,
  polyLine2MergeCoincidentVertices,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { polyLine3OnPlane } from "geom/polyLine3";
import { ascendingSortCompare } from "geom/utils";
import { vec3DistSquared } from "geom/vec3";
import { RBush3D } from "rbush-3d";

// twice the snap rounding tolerance to avoid issues with floating point precision
const MIN_SIMPLIFY_ANGLE_EPSILON = Math.max(1e-2, 2 * SNAP_ROUNDING_TOLERANCE);

const DEFAULT_FACE_MERGE_SIMPLIFY_DIST_EPSILON_SQUARED = 1e-2;

interface SnapVertexBox extends AABB3 {
  /** The actual vertex position */
  x: number;
  y: number;
  z: number;
}

interface FaceGroup {
  readonly index: number;
  readonly faces: Face[];
  readonly tags?: FaceTagBase[];
}

function getFaceTagsHash(tags: FaceTagBase[] | undefined): string {
  if (!tags) {
    return "undefined_tags";
  }

  if (tags.length === 0) {
    return "empty_tags";
  }

  return hashString(JSON.stringify(tags)).toString();
}

function groupFacesByTags(faces: Face[]): Table<FaceGroup> {
  const groups: RWTable<FaceGroup> = {};
  for (const face of faces) {
    const { tags } = face;

    const hash = getFaceTagsHash(tags);
    if (!groups[hash]) {
      groups[hash] = {
        // index is used to preserve the order of the input faces
        // (see comment at mergeCoplanarFaces)
        index: Object.keys(groups).length,
        faces: [],
        tags: tags ? [...tags] : undefined,
      };
    }

    groups[hash].faces.push(face);
  }

  return groups;
}

function filterFaceLoop(
  loop: PolyLine2,
  distThresholdSq: number = DEFAULT_FACE_MERGE_SIMPLIFY_DIST_EPSILON_SQUARED,
): boolean {
  return polyLine2EdgeCount(loop) >= 3 && polyLine2Area(loop) > distThresholdSq;
}

function simplifyFace(
  { loops, plane }: Face,
  distThresholdSq: number = DEFAULT_FACE_MERGE_SIMPLIFY_DIST_EPSILON_SQUARED,
  angleThreshold: number = MIN_SIMPLIFY_ANGLE_EPSILON,
): Face | null {
  // simplify the loops
  let newLoops = loops.map((loop) => {
    const pl = polyLine2RemoveInvalidVertices(polyLine2Simplify(loop, distThresholdSq, angleThreshold), angleThreshold);

    // merging coincident vertices again after removing invalid vertices
    return polyLine2MergeCoincidentVertices(pl, distThresholdSq);
  });

  // filter out faces with invalid outer loop
  if (!filterFaceLoop(newLoops[0])) {
    return null;
  }

  // filter out invalid inner loops
  newLoops = [newLoops[0], ...newLoops.slice(1).filter((loop) => filterFaceLoop(loop, distThresholdSq))];

  return faceNew(newLoops, plane);
}

/**
 * Creates a spatial index of original 3D vertex positions from input faces
 */
function createOriginalVerticesRTree(inputFaces: Face[]): RBush3D {
  const rtree = new RBush3D();
  const originalVertices: SnapVertexBox[] = [];

  for (const { loops, plane } of inputFaces) {
    for (const loop of loops) {
      const loop3 = polyLine3OnPlane(loop, plane);

      // skip the last vertex since it's a closed loop
      for (let i = 0, l = loop3.length - 3; i < l; i += 3) {
        const x = loop3[i];
        const y = loop3[i + 1];
        const z = loop3[i + 2];

        originalVertices.push({
          minX: x - SNAP_ROUNDING_TOLERANCE,
          minY: y - SNAP_ROUNDING_TOLERANCE,
          minZ: z - SNAP_ROUNDING_TOLERANCE,
          maxX: x + SNAP_ROUNDING_TOLERANCE,
          maxY: y + SNAP_ROUNDING_TOLERANCE,
          maxZ: z + SNAP_ROUNDING_TOLERANCE,
          x,
          y,
          z,
        });
      }
    }
  }

  rtree.load(originalVertices);
  return rtree;
}

/**
 * Compensates for tolerance errors by snapping output vertices back to original positions
 */
function compensateToleranceErrors(outputFaces: Face[], originalVerticesRTree: RBush3D): Face[] {
  const compensatedFaces: Face[] = [];

  for (const face of outputFaces) {
    const compensatedLoops: PolyLine2[] = [];

    for (const loop of face.loops) {
      const compensatedLoop = polyLine2New(loop.length);

      for (let i = 0; i < loop.length; i += 2) {
        const u = loop[i];
        const v = loop[i + 1];
        const [x, y, z] = planeEval(face.plane, u, v);

        // Search for nearby original vertices
        const searchBox = {
          minX: x - SNAP_ROUNDING_TOLERANCE,
          minY: y - SNAP_ROUNDING_TOLERANCE,
          minZ: z - SNAP_ROUNDING_TOLERANCE,
          maxX: x + SNAP_ROUNDING_TOLERANCE,
          maxY: y + SNAP_ROUNDING_TOLERANCE,
          maxZ: z + SNAP_ROUNDING_TOLERANCE,
        };

        const nearbyVertices = originalVerticesRTree.search(searchBox) as SnapVertexBox[];

        // Find the closest original vertex within tolerance
        let closestVertex: SnapVertexBox | null = null;
        let closestDistSquared = Number.POSITIVE_INFINITY;

        for (const nearbyVertex of nearbyVertices) {
          const distSquared = vec3DistSquared(x, y, z, nearbyVertex.x, nearbyVertex.y, nearbyVertex.z);
          if (distSquared < closestDistSquared) {
            closestVertex = nearbyVertex;
            closestDistSquared = distSquared;
          }
        }

        if (closestVertex) {
          // Snap back to original position by projecting it to the current face plane
          const [snappedU, snappedV] = planeProject(face.plane, closestVertex.x, closestVertex.y, closestVertex.z);
          compensatedLoop[i] = snappedU;
          compensatedLoop[i + 1] = snappedV;
        } else {
          // Keep original coordinates if no nearby vertex found
          compensatedLoop[i] = u;
          compensatedLoop[i + 1] = v;
        }
      }

      compensatedLoops.push(compensatedLoop);
    }

    compensatedFaces.push(faceNew(compensatedLoops, face.plane, face.tags));
  }

  return compensatedFaces;
}

interface MergeCoplanarFacesOptions {
  readonly inputFaces: Face[];
  readonly coplanarUvAngleTolerance?: number;
  readonly simplifyDistThresholdSq?: number;
  readonly simplifyAngleThreshold?: number;
}

export function mergeCoplanarFaces({
  inputFaces,
  coplanarUvAngleTolerance,
  simplifyDistThresholdSq,
  simplifyAngleThreshold,
}: MergeCoplanarFacesOptions): Face[] {
  const mergedFaces: Face[] = [];

  // group them by face tags
  const faceGroups = groupFacesByTags(inputFaces);

  // Process each group in order of creation. This is needed because we can have face tags referencing a dynamically
  // generated entity id (e.g. parent soffit tag). These ids can change between different runs of the same building generation,
  // so the hashes from the face tags can change between runs. Different face hashes will result in a different order of processing
  // if we do a simple iteration over the groups. We need to make sure the face order is stable or PDF tests will be flaky.
  const groupIds = Object.keys(faceGroups).sort((a, b) =>
    ascendingSortCompare(faceGroups[a].index, faceGroups[b].index),
  );
  for (const id of groupIds) {
    const { faces, tags } = faceGroups[id];
    const coplanarFaceGroups =
      coplanarUvAngleTolerance === undefined
        ? gatherOrientedCoplanarFaceGroupsFast(faces)
        : gatherOrientedCoplanarFaceGroups({ inputFaces: faces, coplanarUvAngleTolerance });

    // merge
    for (const coplanarFaces of coplanarFaceGroups) {
      // create spatial index of original vertex positions
      const originalVerticesRTree = createOriginalVerticesRTree(coplanarFaces);

      let mergedCoplanarFaces = mergeNonAdjacentCoplanarFaces(coplanarFaces, PolygonRoundingMode.SNAP_ROUND);

      // compensate for tolerance errors introduced by the snap rounding
      mergedCoplanarFaces = compensateToleranceErrors(mergedCoplanarFaces, originalVerticesRTree);

      // simplify
      mergedCoplanarFaces = mergedCoplanarFaces
        .map((face) => simplifyFace(face, simplifyDistThresholdSq, simplifyAngleThreshold))
        .filter((face) => !!face);

      // add tags if they exist
      if (tags) {
        mergedCoplanarFaces = tagFaces(mergedCoplanarFaces, tags);
      }

      // add to output
      mergedFaces.push(...mergedCoplanarFaces);
    }
  }

  return mergedFaces;
}

interface MergeCoplanarFaceArraysOptions<T extends FaceArrayViewEntity> {
  readonly faceArrays: T[];
  readonly coplanarUvAngleTolerance?: number;
  readonly simplifyDistThresholdSq?: number;
  readonly simplifyAngleThreshold?: number;
}

export function mergeCoplanarFaceArrays<T extends FaceArrayViewEntity>({
  faceArrays,
  coplanarUvAngleTolerance,
  simplifyDistThresholdSq,
  simplifyAngleThreshold,
}: MergeCoplanarFaceArraysOptions<T>): T[] {
  const mergedFaceArrays: T[] = [];

  for (const faceArray of faceArrays) {
    const mergedFaces = mergeCoplanarFaces({
      inputFaces: faceArray.faces,
      coplanarUvAngleTolerance,
      simplifyDistThresholdSq,
      simplifyAngleThreshold,
    });
    mergedFaceArrays.push({
      ...faceArray,
      faces: mergedFaces,
    });
  }

  return mergedFaceArrays;
}
